import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Instagram, Facebook, Linkedin, Twitter, Clock } from "lucide-react";

export function Footer() {
  return (
    <footer className="bg-navy text-white">
      <div className="container mx-auto px-4 pt-16 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div>
            <div className="bg-white/95 backdrop-blur-sm rounded-xl p-4 border border-gray-200/50 mb-6">
              <Link to="/" className="flex items-center gap-3">
                <div className="relative">
                  <img 
                    src="/assets/images/mainwebsiteLogo.png" 
                    alt="Mega Marine Container Terminals Logo" 
                    className="h-12 w-auto"
                    onError={(e) => {
                      // Try Photo2 as fallback first, then icon fallback
                      const target = e.currentTarget;
                      if (target.src.includes('mainwebsiteLogo.png')) {
                        target.src = '/assets/images/Photo2.jpg';
                        target.className = 'h-12 w-auto object-contain rounded';
                      } else {
                        // Show Container icon as final fallback
                        const container = target.parentElement;
                        if (container) {
                          target.style.display = 'none';
                          container.innerHTML = `
                            <div class="flex items-center gap-2">
                              <svg class="h-10 w-10 text-primary" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M2 6h20v12H2V6zm2 2v8h16V8H4zm2 2h2v4H6v-4zm4 0h4v4h-4v-4zm6 0h2v4h-2v-4z"/>
                              </svg>
                            </div>
                          `;
                        }
                      }
                    }}
                  />
                </div>
                <div className="flex flex-col">
                  <span className="text-xl font-semibold text-gray-800">
                    Mega Marine
                  </span>
                  <span className="text-sm font-medium text-primary/80">
                    Container Terminals
                  </span>
                </div>
              </Link>
            </div>
            <p className="text-gray-300 mb-6">
              Your trusted partner in container yard operations, 
              setting new standards of transparency and quality in the shipping industry.
            </p>
            <div className="flex space-x-4">              <a 
                href="https://facebook.com" 
                className="text-gray-300 hover:text-accent transition-colors"
                aria-label="Follow us on Facebook"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Facebook className="h-6 w-6" />
              </a>
              <a 
                href="https://twitter.com" 
                className="text-gray-300 hover:text-accent transition-colors"
                aria-label="Follow us on Twitter"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Twitter className="h-6 w-6" />
              </a>
              <a 
                href="https://linkedin.com" 
                className="text-gray-300 hover:text-accent transition-colors"
                aria-label="Follow us on LinkedIn"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Linkedin className="h-6 w-6" />
              </a>
              <a 
                href="https://instagram.com" 
                className="text-gray-300 hover:text-accent transition-colors"
                aria-label="Follow us on Instagram"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Instagram className="h-6 w-6" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-xl font-semibold mb-6 flex items-center gap-2">
              {/* Globe/World icon for Quick Links */}
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <circle cx="12" cy="12" r="10" />
                <ellipse cx="12" cy="12" rx="6" ry="10" />
                <path d="M2 12h20" />
                <path d="M12 2a15.3 15.3 0 0 1 0 20" />
                <path d="M12 2a15.3 15.3 0 0 0 0 20" />
              </svg>
              Quick Links
            </h3>
            <ul className="space-y-3">
              <li>
                <Link 
                  to="/" 
                  className="text-gray-300 hover:text-accent transition-colors"
                  onClick={() => window.scrollTo(0, 0)}
                >
                  Home
                </Link>
              </li>
              <li>
                <Link 
                  to="/about" 
                  className="text-gray-300 hover:text-accent transition-colors"
                  onClick={() => window.scrollTo(0, 0)}
                >
                  About Us
                </Link>
              </li>
              <li>
                <Link 
                  to="/services" 
                  className="text-gray-300 hover:text-accent transition-colors"
                  onClick={() => window.scrollTo(0, 0)}
                >
                  Services
                </Link>
              </li>
              <li>
                <Link 
                  to="/why-choose-us" 
                  className="text-gray-300 hover:text-accent transition-colors"
                  onClick={() => window.scrollTo(0, 0)}
                >
                  Why Choose Us
                </Link>
              </li>
              <li>
                <Link 
                  to="/clients" 
                  className="text-gray-300 hover:text-accent transition-colors"
                  onClick={() => window.scrollTo(0, 0)}
                >
                  Clients
                </Link>
              </li>
              <li>
                <Link 
                  to="/tariff" 
                  className="text-gray-300 hover:text-accent transition-colors"
                  onClick={() => window.scrollTo(0, 0)}
                >
                  Tariff
                </Link>
              </li>
              <li>
                <Link 
                  to="/contact" 
                  className="text-gray-300 hover:text-accent transition-colors"
                  onClick={() => window.scrollTo(0, 0)}
                >
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-xl font-semibold mb-6 flex items-center gap-2">
              <Phone className="h-5 w-5 text-accent" /> Contact Us
            </h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-accent mt-1 shrink-0" />
                <span className="text-gray-300">
                  Pappu Arcade Survey no. 188, Plot No. 58/1 Suraj Nagar, Baroi, Mundra, Kachchh, Gujarat- 370421
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="h-5 w-5 text-accent shrink-0" />
                <div className="flex flex-col space-y-1">
                  <a
                    href="tel:+919687990249"
                    className="text-gray-300 hover:text-accent transition-colors"
                  >
                    +91 96879 90249
                  </a>
                  <a
                    href="tel:+919979533765"
                    className="text-gray-300 hover:text-accent transition-colors"
                  >
                    +91 99795 33765
                  </a>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-accent shrink-0" />
                <a 
                  href="mailto:info@megamarinecontainer.com" 
                  className="text-gray-300 hover:text-accent transition-colors"
                >
                  info@megamarinecontainer.com
                </a>
              </li>
            </ul>
          </div>

          {/* Business Hours */}
          <div>
            <h3 className="text-xl font-semibold mb-6 flex items-center gap-2">
              <Clock className="h-5 w-5 text-accent" /> Business Hours
            </h3>
            <div className="ml-2">
              <span className="text-accent font-semibold text-base md:text-lg">We are here to serve you any time any day</span>
              <span className="block text-accent/80 font-medium text-sm md:text-base mt-1">Available 24/7 — Because your cargo never sleeps.</span>
            </div>
          </div>
        </div>

        <hr className="border-gray-800 my-8" />

        <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-xs text-gray-400 text-center mt-6 font-medium" style={{ fontFamily: 'Poppins, Inter, Nunito Sans, Roboto, Arial, sans-serif', letterSpacing: '0.02em' }}>
            © {new Date().getFullYear()} Mega Marine Container Terminals. All rights reserved. |
            Developed by <a href="https://aayushkr.vercel.app/" target="_blank" rel="noopener noreferrer" className="text-accent font-semibold hover:underline">Aayush Kumar</a>
          </p>
          <div className="flex gap-4 text-sm">            <a 
              href="/privacy-policy" 
              className="text-gray-300 hover:text-accent transition-colors"
            >
              Privacy Policy
            </a>
            <a 
              href="/terms-of-service" 
              className="text-gray-300 hover:text-accent transition-colors"
            >
              Terms of Service
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
